import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import CreateCampaignForm from "../../components/forms/campaigns/CreateCampaignForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../shared/utility";

export function CampaignsViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState({ data: [], total: 0 });
  const [productsForDropdown, setProductsForDropdown] = useState([]);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const getCampaigns = async function (search, page) {
    setLoading(true);
    try {
      const payload = {
        page: page || 1,
        search: search ?? null,
      };
      const callResult = await makePostApiCall("campaigns", payload);
      if (callResult.success) {
        setCampaigns(callResult.data);
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async function (values) {
    const payload = { ...values };
    const callResult = await makePostApiCall("createCampaign", payload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Campaign added successfully",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      setTimeout(function () {
        getCampaigns(null, 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Campaign Error",
          callResult.data?.message || "Could not create campaign",
        );
      }
    }
  };

  const fetchProductsForDropdown = async function () {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);

    if (callResult.success) {
      setProductsForDropdown(callResult.data);
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return [];
    }
  };

  const fetchCampaignUsersForDropdown = async function () {
    const callResult = await makeGetApiCall("campaignUsersForDropdown");
    if (callResult.success) {
      return callResult.data ?? [];
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    return [];
  };

  const openCreateCampaignSlideOver = async () => {
    const [products, campaignUsers] = await Promise.all([
      fetchProductsForDropdown(),
      fetchCampaignUsersForDropdown(),
    ]);
    setSlideOver({
      childComponent: (
        <CreateCampaignForm
          setOpen={setSlideOver}
          actionCall={createCampaign}
          productsForDropdown={products}
          campaignUsersForDropdown={campaignUsers}
        />
      ),
      open: true,
    });
  };

  const launchCampaign = async function (id) {
    const callResult = await makeGetApiCall(`startCampaign/${id}`, {});
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Campaign launched successfully",
          SnackbarType.Success,
        ),
      );
      return { success: true };
    } else if (callResult.errorStatus === 401) {
      logout();
      return { success: false };
    } else {
      return { success: false };
    }
  };

  const deleteCampaign = async function (id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall(`deleteCampaign/${id}`, {});
      if (callResult.success) {
        setEmptyModelOpen({
          open: false,
          childComponent: null,
        });
        getCampaigns(null, 1);
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data.message || "Campaign deleted successfully",
            SnackbarType.Success,
          ),
        );
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const askDeleteCampaign = function (campaign) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteCampaign(campaign.id)}
          message={`Are you sure you want to delete ${campaign.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteCampaign,
    campaigns,
    emptyModal,
    getCampaigns,
    loading,
    openCreateCampaignSlideOver,
    productsForDropdown,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    fetchProductsForDropdown,
    launchCampaign,
  };
}
